import { useEffect, useRef } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useGlobalTimers } from '../../context/GlobalTimersContext';
import { playGoalReachedSound, isSoundEnabled } from '../../utils/soundEffects';
import { updateMemberTotalPoints } from '../../services/pointsService';
import { useToast } from '../ui/toast/ToastProvider';
import { useTranslation } from 'react-i18next';
import { getLocalDateKey } from '../../utils/dateKey';

/**
 * Componente invisible que maneja el autocompletado de timers
 * Montado en AppLayout para que funcione aunque cambies de página
 */
export function TimerManager() {
  const { timers, getTimerState, stopTimer, completeTimerById } = useGlobalTimers();
  const { showToast } = useToast();
  const { t } = useTranslation();
  
  // Track de timers ya procesados para evitar duplicados
  const processedTimers = useRef<Set<string>>(new Set());
  const soundPlayed = useRef<Set<string>>(new Set());

  useEffect(() => {
    timers.forEach(timer => {
      if (!timer.running) return;
      if (processedTimers.current.has(timer.id)) return;

      const state = getTimerState(timer);
      const { totalElapsedSec, phase } = state;
      const elapsedMinutes = totalElapsedSec / 60;
      const { durationMinutes, completionGoal, graceMinutes } = timer;

      // 🎯 Autocompletar según completionGoal
      if (completionGoal === 'complete_time') {
        // Modo 1: Autocompletar cuando se alcanza el tiempo objetivo
        if (elapsedMinutes >= durationMinutes && durationMinutes > 0) {
          // Reproducir sonido de alarma (solo una vez)
          if (!soundPlayed.current.has(timer.id) && isSoundEnabled()) {
            playGoalReachedSound();
            soundPlayed.current.add(timer.id);
          }

          // Autocompletar después de un breve delay
          setTimeout(() => {
            if (!processedTimers.current.has(timer.id)) {
              processedTimers.current.add(timer.id);
              handleAutoComplete(timer, state);
            }
          }, 800);
        }
      } else if (completionGoal === 'before_time') {
        // Modo 2: Alarma cuando se agota el tiempo + gracia (fase red)
        if (phase === 'red' && !soundPlayed.current.has(timer.id)) {
          if (isSoundEnabled()) {
            playGoalReachedSound();
            soundPlayed.current.add(timer.id);
          }
        }
        
        // En 'before_time' NO autocompletamos, el usuario debe hacerlo manualmente
      }
    });
  }, [timers, getTimerState]);

  // ✅ Day change handling: if timer was paused and day changed, apply penalty (complete_time)
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    timers.forEach((timer) => {
      if (timer.running) return;

      const key = `${timer.id}__day_change`;
      if (processedTimers.current.has(key)) return;

      try {
        const timerDate = timer.createdAt?.toDate ? timer.createdAt.toDate().toISOString().slice(0, 10) : null;
        if (!timerDate || timerDate === today) return;

        if (timer.completionGoal !== 'complete_time') {
          // Mantener comportamiento existente: limpiar silenciosamente
          processedTimers.current.add(key);
          void stopTimer(timer.id);
          return;
        }

        processedTimers.current.add(key);
        void completeTimerById(timer.id, { reason: 'day_change' });
      } catch {
        processedTimers.current.add(key);
        void stopTimer(timer.id);
      }
    });
  }, [timers, stopTimer, completeTimerById]);

  const handleAutoComplete = async (timer: typeof timers[0], state: ReturnType<typeof getTimerState>) => {
    const { totalElapsedSec } = state;
    const elapsedMinutes = totalElapsedSec / 60;
    let pointsToApply = timer.rewardPoints;

    // Calcular puntos según completionGoal
    if (timer.completionGoal === 'complete_time') {
      // Ya cumplió el tiempo mínimo, dar puntos completos
      pointsToApply = timer.rewardPoints;
    } else if (timer.completionGoal === 'before_time') {
      // Verificar si está fuera de tiempo
      if (elapsedMinutes > timer.durationMinutes + timer.graceMinutes) {
        const penaltyMode = timer.latePenaltyMode || 'full';
        if (penaltyMode === 'full') {
          pointsToApply = -timer.rewardPoints;
        } else if (penaltyMode === 'half') {
          pointsToApply = -Math.floor(timer.rewardPoints / 2);
        }
      }
    }

    try {
      // Guardar en daily_log
      const dateToday = getLocalDateKey();
      const timeOfDayArray = Array.isArray(timer.timeOfDay) ? timer.timeOfDay : [];
      const completedPeriod = timeOfDayArray.length > 0 ? (timer.startedPeriod || null) : null;
      
      await addDoc(collection(db, 'daily_log'), {
        type: 'activity',
        activityId: timer.activityId,
        memberId: timer.memberId,
        familyId: timer.familyId,
        points: pointsToApply,
        status: 'completed',
        completed: true,
        date: dateToday,
        createdAt: serverTimestamp(),
        memberName: timer.memberName,
        memberAvatar: timer.memberAvatar,
        activityName: timer.activityName,
        activityIcon: timer.activityIcon,
        completedAt: serverTimestamp(),
        elapsedMinutes: Math.round(elapsedMinutes * 10) / 10,
        phase: state.phase,
        completionGoal: timer.completionGoal,
        completionReason: 'auto',
        timeOfDay: timeOfDayArray,
        ...(completedPeriod ? { completedPeriod } : {}),
      });

      // Actualizar totalPoints del miembro
      await updateMemberTotalPoints(timer.memberId, pointsToApply, 'activity');

      // Eliminar el timer de Firestore
      await stopTimer(timer.id);

      // Mostrar notificación
      showToast?.({
        type: pointsToApply > 0 ? 'success' : 'warning',
        message: t('activities.autoCompletedToast', {
          defaultValue: '{{memberName}} completó "{{activityName}}" ({{points}} pts)',
          memberName: timer.memberName,
          activityName: timer.activityName,
          points: `${pointsToApply > 0 ? '+' : ''}${pointsToApply}`,
        }) as string,
      });
    } catch (error) {
      processedTimers.current.delete(timer.id); // Permitir reintento
    }
  };

  return null; // Componente invisible
}
